
import React, { useMemo, useState } from 'react';
import { User, UserPrivilege, Brand } from '../../../types';
import { UserRole, Department, AdminView } from '../../../enums';
import { ALL_PERMISSIONS, BASIC_PERMISSIONS } from '../../../constants';
import { canonicalizePermissions } from '../../../utils/permissions';
import { Plus, Edit2, Trash2, UserCheck, UserX, Search, Mail, Shield, CheckSquare, Square, Lock, Key, Upload, User as UserIcon, X, ToggleLeft, ToggleRight, AlertTriangle, Check, XCircle, CheckCircle, LayoutDashboard } from 'lucide-react';
import { apiClient } from '../../../services/apiClient';

interface UserManagementViewProps {
  users: User[];
  brands: Brand[];
  onUpdateUsers: (users: User[]) => void;
  currentUser: User;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  onViewManagerDashboard?: (user: User) => void;
}

const PERMISSION_OPTIONS = [
  { id: AdminView.DASHBOARD, label: 'Dashboard' },
  { id: AdminView.CAMPAIGNS, label: 'Campaigns' },
  { id: AdminView.SUBMISSIONS, label: 'Submissions' },
  { id: AdminView.PROPOSALS, label: 'Proposals' },
  { id: AdminView.TABLE, label: 'Master Data Table' },
  { id: AdminView.REPORTS, label: 'Reports & Exports' },
  { id: AdminView.ANALYTICS, label: 'Analytics' },
  { id: AdminView.COMMUNITY, label: 'Community' },
  { id: AdminView.MENU_BUILDER, label: 'Menu Builder' },
  { id: AdminView.SETTINGS, label: 'Settings' },
  { id: AdminView.USERS, label: 'User Management' },
  { id: AdminView.PROFILE, label: 'Profile' },
  { id: AdminView.TRACE, label: 'Trace / Track' },
  { id: AdminView.MANAGER_DASHBOARD, label: 'Manager Dashboard' },
  { id: AdminView.MANAGERS_DASHBOARD, label: 'Managers Dashboard (Admin)' },
  // Action permissions
  { id: AdminView.SUBMIT_SUBMISSIONS, label: 'Can Submit (Submissions)', isAction: true },
  { id: AdminView.SUBMIT_PROPOSALS, label: 'Can Submit (Proposals)', isAction: true },
  { id: AdminView.SUBMIT_CAMPAIGNS, label: 'Can Create Campaigns', isAction: true },
];

const ADMIN_ONLY_PERMS = new Set<AdminView>([
  AdminView.USERS,
  AdminView.SETTINGS,
  AdminView.TRACE,
  AdminView.SUBMIT_CAMPAIGNS, // Only admins can create campaigns
  AdminView.MANAGERS_DASHBOARD, // Admin-only: view any manager's dashboard
]);

export const UserManagementView: React.FC<UserManagementViewProps> = ({ users, brands, onUpdateUsers, currentUser, showToast, onViewManagerDashboard }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'permissions'>('details');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const initialFormState: User = {
    id: '',
    name: '',
    email: '',
    username: '',
    password: '',
    role: UserRole.DEPT_USER,
    department: Department.FB,
    active: true,
    permissions: canonicalizePermissions(BASIC_PERMISSIONS),
    profileImage: undefined,
    privilege: 'OPERATION',
    brandIds: []
  };

  const [formData, setFormData] = useState<User>(initialFormState);
  const visiblePermissionOptions = useMemo(
    () =>
      PERMISSION_OPTIONS.filter((opt) =>
        formData.privilege === 'ADMIN' ? true : !ADMIN_ONLY_PERMS.has(opt.id)
      ),
    [formData.privilege]
  );
  const brandLookup = useMemo(() => {
    const map = new Map<string, Brand>();
    brands.forEach((b) => map.set(b.id, b));
    return map;
  }, [brands]);

  // Separate pending users from active/inactive list
  const pendingUsers = users.filter(u => u.isPending);
  const activeUsers = users.filter(u => !u.isPending).filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

    const handleEdit = (user: User) => {
        const normalizedPerms = canonicalizePermissions(user.permissions);
        setFormData({ ...user, password: '', brandIds: user.brandIds || [], permissions: normalizedPerms });
        setActiveTab('details');
        setIsEditing(true);
    };

  const handleCreate = () => {
    setFormData({ ...initialFormState, id: Date.now().toString(), brandIds: [] });
    setActiveTab('details');
    setIsEditing(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onloadend = () => {
              setFormData({ ...formData, profileImage: reader.result as string });
          };
          reader.readAsDataURL(file);
      }
  };

  const removeImage = () => {
      setFormData({...formData, profileImage: undefined});
  }

  const toggleBrandAssignment = (brandId: string) => {
    const current = formData.brandIds || [];
    const updated = current.includes(brandId)
      ? current.filter(id => id !== brandId)
      : [...current, brandId];
    setFormData({ ...formData, brandIds: updated });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.username || !formData.email) {
      showToast('error', 'Validation Error', 'Name, Username and Email are required.');
      return;
    }

    if (users.some(u => u.username === formData.username && u.id !== formData.id)) {
       showToast('error', 'Duplicate User', 'Username already exists.');
       return;
    }

    const cleanedPermissions = canonicalizePermissions(formData.permissions);

    const updatedUser: any = {
        ...formData,
        isPending: false,
        active: true,
        permissions: cleanedPermissions
    };

    if (!updatedUser.password) {
        delete updatedUser.password;
    }

    try {
      setSaving(true);
      if (users.some(u => u.id === formData.id)) {
        const list = await apiClient.users.update(formData.id, updatedUser);
        if (Array.isArray(list)) onUpdateUsers(list);
        else onUpdateUsers(users.map(u => u.id === formData.id ? updatedUser : u));
        showToast('success', 'User Updated', `User ${formData.name} updated successfully.`);
      } else {
        const created: any = await apiClient.auth.register({
          name: formData.name,
          email: formData.email,
          username: formData.username,
          password: formData.password || 'password'
        });
        const newId = created?.id || formData.id;
        if (newId) {
          const list = await apiClient.users.update(newId, { ...updatedUser, id: newId });
          if (Array.isArray(list)) {
            onUpdateUsers(list);
          } else {
            onUpdateUsers([...users, { ...updatedUser, id: newId }]);
          }
        }
        showToast('success', 'User Created', `User ${formData.name} created successfully.`);
      }
      setIsEditing(false);
    } catch (error) {
      showToast('error', 'Save Failed', 'Unable to persist user changes.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (user: User) => {
    if (user.id === currentUser.id) {
      showToast('error', 'Action Denied', 'You cannot deactivate your own account.');
      return;
    }
    const updatedUser = { ...user, active: !user.active };
    try {
      await apiClient.users.update(user.id, { active: updatedUser.active });
      const updated = users.map(u => u.id === user.id ? updatedUser : u);
      onUpdateUsers(updated);
      showToast('info', 'Status Changed', `User ${user.name} is now ${updatedUser.active ? 'Active' : 'Inactive'}.`);
    } catch (error) {
      showToast('error', 'Update Failed', 'Could not update user status.');
    }
  };

  const handleReject = async (userId: string) => {
      if (confirm("Are you sure you want to reject and delete this registration request?")) {
          try {
            await apiClient.users.update(userId, { isPending: false, active: false });
          } catch (error) {
            console.warn('Failed to persist rejection, updating locally.', error);
          }
          onUpdateUsers(users.filter(u => u.id !== userId));
          showToast('info', 'Registration Rejected', 'User request has been deleted.');
      }
  };

  const togglePermission = (view: AdminView) => {
      const current = formData.permissions;
      let updated: AdminView[];
      if (current.includes(view)) {
          updated = current.filter(p => p !== view);
      } else {
          updated = [...current, view];
      }
      setFormData({ ...formData, permissions: canonicalizePermissions(updated) });
  };

  const toggleAllPermissions = () => {
      const visibleIds = visiblePermissionOptions.map((o) => o.id);
      if (formData.permissions.length === visibleIds.length) {
          setFormData({ ...formData, permissions: [] });
      } else {
          setFormData({ ...formData, permissions: canonicalizePermissions(visibleIds) });
      }
  };

  const handlePrivilegeChange = (privilege: UserPrivilege) => {
      let newRole = formData.role;
      let newPermissions = formData.permissions;

      if (privilege === 'ADMIN') {
          newRole = UserRole.ADMIN;
          newPermissions = canonicalizePermissions(ALL_PERMISSIONS);
      } else {
          newRole = UserRole.DEPT_USER;
          newPermissions = canonicalizePermissions(
            BASIC_PERMISSIONS.filter((p) => !ADMIN_ONLY_PERMS.has(p))
          );
      }

      setFormData({
          ...formData,
          privilege,
          role: newRole,
          permissions: newPermissions
      });
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-3xl border border-zinc-200 shadow-lg p-8 animate-fade-in-up max-w-3xl mx-auto">
         <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-zinc-900">
                {users.some(u => u.id === formData.id) ? 'Edit User' : 'Create New User'}
            </h2>
            <div className="bg-zinc-100 rounded-lg p-1 flex">
                <button 
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'details' ? 'bg-white shadow text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                    Profile Details
                </button>
                <button 
                    onClick={() => setActiveTab('permissions')}
                    className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'permissions' ? 'bg-white shadow text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                    Access Permissions
                </button>
            </div>
         </div>
         
         {activeTab === 'details' ? (
             <div className="space-y-6 animate-fade-in">
                
                {/* User Privilege Toggle */}
                <div className="bg-zinc-50 p-4 rounded-2xl border border-zinc-200 flex justify-between items-center">
                    <div>
                        <h4 className="text-sm font-bold text-zinc-900">User Privilege</h4>
                        <p className="text-xs text-zinc-500">Determines level of access and default role.</p>
                    </div>
                    <div className="bg-white p-1 rounded-xl border border-zinc-200 flex shadow-sm">
                        <button 
                            onClick={() => handlePrivilegeChange('OPERATION')}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${formData.privilege === 'OPERATION' ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-900'}`}
                        >
                            Operation
                        </button>
                        <button 
                            onClick={() => handlePrivilegeChange('ADMIN')}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${formData.privilege === 'ADMIN' ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-900'}`}
                        >
                            Admin
                        </button>
                    </div>
                </div>

                {/* Profile Image Section */}
                <div className="flex justify-center mb-4">
                    <label className="relative cursor-pointer group">
                        <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                        <div className="w-24 h-24 rounded-full bg-zinc-100 border-2 border-dashed border-zinc-300 flex items-center justify-center overflow-hidden group-hover:border-teal-400 transition-colors">
                            {formData.profileImage ? (
                                <img src={formData.profileImage} alt="current" className="w-full h-full object-cover" />
                            ) : (
                                <UserIcon className="w-8 h-8 text-zinc-300 group-hover:text-teal-400" />
                            )}
                        </div>
                        <div className="absolute bottom-0 right-0 bg-teal-500 p-1.5 rounded-full text-white shadow-md group-hover:scale-110 transition-transform">
                            <Upload className="w-3 h-3" />
                        </div>
                        {formData.profileImage && (
                            <button 
                                onClick={(e) => { e.preventDefault(); removeImage(); }}
                                className="absolute top-0 right-0 bg-red-500 p-1 rounded-full text-white shadow-md hover:bg-red-600"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Full Name</label>
                    <input 
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                        autoComplete="name"
                    />
                    </div>
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Email Address</label>
                    <input 
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                        autoComplete="email"
                    />
                    </div>
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Username</label>
                    <input 
                        type="text"
                        value={formData.username}
                        onChange={e => setFormData({...formData, username: e.target.value})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                        autoComplete="username"
                    />
                    </div>
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Password</label>
                    <input 
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={e => setFormData({...formData, password: e.target.value})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                        placeholder="Leave blank to keep current password"
                        autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-xs font-bold text-teal-600 hover:text-teal-700"
                    >
                      {showPassword ? 'Hide Password' : 'Show Password'}
                    </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-zinc-100">
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Role</label>
                    <select
                        value={formData.role}
                        onChange={e => {
                            const newRole = e.target.value as UserRole;
                            let newPermissions = formData.permissions;
                            // Auto-assign core permissions for Managers if not present
                            if (newRole === UserRole.MANAGER) {
                                const required = [
                                  AdminView.SUBMIT_SUBMISSIONS,
                                  AdminView.SUBMIT_PROPOSALS,
                                  AdminView.MANAGER_DASHBOARD,
                                  AdminView.PROFILE
                                ];
                                required.forEach(view => {
                                  if (!newPermissions.includes(view)) {
                                    newPermissions = [...newPermissions, view];
                                  }
                                });
                            }
                            setFormData({...formData, role: newRole, permissions: newPermissions});
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                    >
                        <option value={UserRole.DEPT_USER}>Department User</option>
                        <option value={UserRole.MANAGER}>Manager</option>
                        <option value={UserRole.SEO}>SEO</option>
                        <option value={UserRole.ADMIN}>Admin</option>
                    </select>
                    </div>
                    <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase">Department</label>
                    <select
                        value={formData.department}
                        onChange={e => setFormData({...formData, department: e.target.value as Department})}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                    >
                        <option value={Department.FB}>{Department.FB}</option>
                        <option value={Department.FP}>{Department.FP}</option>
                        <option value={Department.DS}>{Department.DS}</option>
                        <option value={Department.OPERATIONS}>{Department.OPERATIONS}</option>
                        <option value={Department.ALL}>{Department.ALL}</option>
                    </select>
                    <p className="text-[10px] text-zinc-400 italic">Admins and SEOs should usually have "All Departments"</p>
                    </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-zinc-100">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-zinc-500 uppercase">Brands</label>
                        <span className="text-[10px] text-zinc-400 uppercase font-bold">Multi-brand ready</span>
                    </div>
                    {brands.length === 0 ? (
                        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-xs text-zinc-500">
                            No brands available yet. Create brands first to assign them.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {brands.map((brand) => {
                                const checked = (formData.brandIds || []).includes(brand.id);
                                return (
                                    <label
                                      key={brand.id}
                                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
                                      onClick={() => toggleBrandAssignment(brand.id)}
                                    >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleBrandAssignment(brand.id)}
                                          className="w-4 h-4 accent-teal-600"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="font-bold text-sm">{brand.name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <p className="text-[10px] text-zinc-400 italic">Assign one or more brands to managers. Leave empty for all brands.</p>
                </div>
            </div>
         ) : (
            <div className="space-y-6 animate-fade-in">
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 mb-4">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Permissions define which tabs appear in the user's sidebar.
                    </p>
                </div>

                <div className="flex justify-end mb-2">
                    <button onClick={toggleAllPermissions} className="text-xs font-bold text-teal-600 hover:underline">
                        Toggle All
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {visiblePermissionOptions.map((option) => {
                        const isEnabled = formData.permissions.includes(option.id);
                        const isAction = (option as any).isAction;
                        return (
                            <div 
                                key={option.id}
                                onClick={() => togglePermission(option.id)}
                                className={`
                                    flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all
                                    ${isEnabled 
                                      ? (isAction ? 'bg-emerald-600 border-emerald-600 text-white shadow-md' : 'bg-zinc-900 border-zinc-900 text-white shadow-md') 
                                      : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50'}
                                `}
                            >
                                {isEnabled ? <CheckSquare className={`w-5 h-5 ${isAction ? 'text-emerald-200' : 'text-teal-400'}`} /> : <Square className="w-5 h-5" />}
                                <span className="font-bold text-sm">{option.label}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
         )}

         <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-zinc-100">
            <button onClick={() => setIsEditing(false)} className="px-6 py-3 text-zinc-500 font-bold hover:bg-zinc-50 rounded-xl transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving} className={`px-6 py-3 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-800 shadow-lg shadow-zinc-900/10 transition-all ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {saving ? 'Saving...' : formData.isPending ? 'Approve & Create' : 'Save User'}
            </button>
         </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in space-y-6">
       <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
             <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">User Management</h2>
             <p className="text-zinc-500 text-sm">Create and manage system access for staff.</p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
             <div className="relative flex-grow md:flex-grow-0">
                <input 
                  type="text" 
                  placeholder="Search users..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm font-semibold text-zinc-700 outline-none focus:ring-2 focus:ring-teal-100 w-full md:w-64 shadow-sm"
                />
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
             </div>
             <button onClick={handleCreate} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-xl font-bold shadow-md shadow-teal-200 transition-all whitespace-nowrap">
                <Plus className="w-4 h-4" /> New User
             </button>
          </div>
       </div>

       {/* PENDING APPROVALS SECTION */}
       {pendingUsers.length > 0 && (
           <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200 shadow-sm animate-fade-in mb-6">
               <div className="flex items-center gap-3 mb-4">
                   <div className="bg-amber-100 p-2 rounded-lg text-amber-700">
                       <AlertTriangle className="w-5 h-5" />
                   </div>
                   <div>
                       <h3 className="text-lg font-bold text-amber-900">Pending Approvals</h3>
                       <p className="text-xs text-amber-700">These users have requested access. Please set their roles to approve.</p>
                   </div>
               </div>

               <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                   {pendingUsers.map(user => (
                       <div key={user.id} className="bg-white p-4 rounded-xl border border-amber-100 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                           <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center font-bold text-zinc-400 text-sm">
                                    {user.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-bold text-zinc-900">{user.name}</div>
                                    <div className="text-xs text-zinc-500">@{user.username} • {user.email}</div>
                                </div>
                           </div>
                           <div className="flex gap-2 w-full sm:w-auto">
                               <button 
                                onClick={() => handleEdit(user)}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                               >
                                   <Check className="w-3.5 h-3.5" /> Approve
                               </button>
                               <button 
                                onClick={() => handleReject(user.id)}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-red-200 text-red-500 hover:bg-red-50 rounded-lg text-xs font-bold transition-colors"
                               >
                                   <XCircle className="w-3.5 h-3.5" /> Reject
                               </button>
                           </div>
                       </div>
                   ))}
               </div>
           </div>
       )}

       {/* ACTIVE USERS GRID */}
       <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {activeUsers.map(user => (
             <div key={user.id} className={`bg-white rounded-2xl p-6 border transition-all ${user.active ? 'border-zinc-200 shadow-sm' : 'border-zinc-100 bg-zinc-50 opacity-75'}`}>
                <div className="flex justify-between items-start mb-4">
                   <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white overflow-hidden ${user.privilege === 'ADMIN' ? 'bg-zinc-900' : 'bg-teal-500'}`}>
                         {user.profileImage ? (
                             <img src={user.profileImage} alt={user.name} className="w-full h-full object-cover" />
                         ) : (
                             user.name.charAt(0)
                         )}
                      </div>
                      <div>
                         <h3 className="font-bold text-zinc-900 text-sm">{user.name}</h3>
                         <span className="text-xs text-zinc-500">@{user.username}</span>
                      </div>
                   </div>
                   <div className="flex gap-2">
                       <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-500`}>
                           {user.privilege}
                       </span>
                       <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${user.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                          {user.active ? 'Active' : 'Inactive'}
                       </span>
                   </div>
                </div>

                <div className="space-y-2 mb-6">
                   <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <Mail className="w-3.5 h-3.5 text-zinc-400" />
                      {user.email}
                   </div>
                   <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <Shield className="w-3.5 h-3.5 text-zinc-400" />
                      {user.role}
                   </div>
                   <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <Key className="w-3.5 h-3.5 text-zinc-400" />
                      {user.permissions.length} views accessible
                   </div>
                   {user.permissions.includes(AdminView.SUBMIT_SUBMISSIONS) && (
                       <div className="flex items-center gap-2 text-xs text-emerald-600 font-bold">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Can Submit
                       </div>
                   )}
                   <div className="inline-block bg-zinc-100 px-2 py-1 rounded text-[10px] font-bold text-zinc-500 uppercase mt-1">
                      {user.department}
                   </div>
                   <div className="flex flex-wrap gap-2 mt-2">
                      {(user.brandIds && user.brandIds.length > 0) ? (
                        user.brandIds.map((bid) => (
                          <span key={bid} className="px-2 py-1 rounded-lg text-[10px] font-bold bg-teal-50 text-teal-700">
                            {brandLookup.get(bid)?.name || bid}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] text-zinc-400 font-bold">All Brands</span>
                      )}
                   </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-zinc-100">
                   <button 
                     onClick={() => handleEdit(user)}
                     className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold text-zinc-600 hover:bg-zinc-50 transition-colors"
                   >
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                   </button>
                   {user.role === UserRole.MANAGER && onViewManagerDashboard && (
                     <button 
                       onClick={() => onViewManagerDashboard(user)}
                       className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold text-teal-600 hover:bg-teal-50 transition-colors"
                     >
                        <LayoutDashboard className="w-3.5 h-3.5" /> View Dashboard
                     </button>
                   )}
                   <button 
                     onClick={() => toggleStatus(user)}
                     className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-colors ${user.active ? 'text-red-500 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                   >
                      {user.active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />} 
                      {user.active ? 'Deactivate' : 'Activate'}
                   </button>
                </div>
             </div>
          ))}
       </div>
    </div>
  );
};
